import { useMutation } from "@tanstack/react-query";
import * as api from "./api";
import { useQuoteStore } from "./store";
import { quoteSnapshotFrom } from "./quoteFormat";

/**
 * useSendQuote: 把一句话发给 /api/smart-quote, 自动:
 *  - 写乐观 user 气泡 → 收到响应后用 session_turns 整体替换
 *  - 服务端 session_id="" 表示报价闭合, 关闭本会话
 *  - 服务端报价附带 calculation_details / total_price, 由调用方决定怎么渲染
 */
export function useSendQuote() {
  const store = useQuoteStore.getState;

  return useMutation({
    mutationFn: async (text: string) => {
      const sessionId = store().ensureSessionId();
      store().beginSend(text);
      try {
        const resp = await api.smartQuote({ text, session_id: sessionId });
        // 服务端响应整体替换 turns
        if (resp.session_turns && resp.session_turns.length > 0) {
          store().replaceTurns(resp.session_turns);
        } else if (resp.session_id === "" && resp.quote_response) {
          // 兜底: 服务端没回 turns 时, 本地把最终回复补进历史
          store().replaceTurns([
            // 本地已挂的报价卡片由 replaceTurns 按 seq 保留, 这里只传消息本身
            ...store()
              .turns.filter((t) => t.seq !== -1)
              .map(({ quote: _quote, ...t }) => t),
            {
              seq: Date.now(),
              role: "assistant",
              content: resp.quote_response,
              ts: new Date().toISOString(),
            },
          ]);
        }
        const snapshot = quoteSnapshotFrom(resp);
        if (snapshot) store().attachQuote(snapshot);
        if (resp.session_id === "") {
          // 报价闭合: 当前轮消息归档 (屏幕保留), 换新会话 id 开启下一轮;
          // 服务端已把完整对话留档到问答记录
          store().archiveRound();
        }
        return resp;
      } catch (e) {
        store().rollbackOptimistic();
        throw e;
      } finally {
        store().finishPending();
      }
    },
  });
}

export function useParseImageRequest() {
  return useMutation({ mutationFn: api.parseImageRequest });
}

export function useGenerateImage() {
  return useMutation({ mutationFn: api.generateImage });
}
